import { useCallback, useState } from "react";
import {
  useLazyGetGroupMembersQuery,
  useLazyGetUserGroupsQuery,
} from "../../infrastructure/api/membersGroupsApi";
import type { Member } from "../../domain/entities/Member";

const MEMBERS_PAGE_SIZE = 100;
// Parallel /hospital-groups/users/{id} lookups per batch.
const LOOKUP_BATCH_SIZE = 6;

/**
 * A group can't be deleted while any of its members belongs to no other
 * group. The API doesn't report this, so we list the group's members and
 * look up each one's groups.
 */
export const useGroupDeletionCheck = () => {
  const [fetchGroupMembers] = useLazyGetGroupMembersQuery();
  const [fetchUserGroups] = useLazyGetUserGroupsQuery();
  const [checking, setChecking] = useState(false);

  const findMembersOnlyInGroup = useCallback(
    async (groupId: string): Promise<Member[]> => {
      setChecking(true);
      try {
        const members: Member[] = [];
        for (let page = 1; ; page++) {
          const batch = await fetchGroupMembers({
            groupId,
            page,
            pageSize: MEMBERS_PAGE_SIZE,
          }).unwrap();
          members.push(...batch);
          if (batch.length < MEMBERS_PAGE_SIZE) break;
        }

        const onlyInGroup: Member[] = [];
        for (let i = 0; i < members.length; i += LOOKUP_BATCH_SIZE) {
          const batch = members.slice(i, i + LOOKUP_BATCH_SIZE);
          const groupsPerMember = await Promise.all(
            batch.map((member) => fetchUserGroups(member.id).unwrap()),
          );
          batch.forEach((member, index) => {
            const groups = groupsPerMember[index];
            if (groups.length > 0 && groups.every((group) => group.id === groupId)) {
              onlyInGroup.push(member);
            }
          });
        }
        return onlyInGroup;
      } finally {
        setChecking(false);
      }
    },
    [fetchGroupMembers, fetchUserGroups],
  );

  return { findMembersOnlyInGroup, checking };
};
